import React, { useEffect, useRef, useState, useCallback } from 'react';
import '../../assets/css/import.scss';
import "../../config";
import { useGlobalPopup } from "../../contexts/GlobalPopupContext";
import ImageEditor from '../Modals/ImageEditor';
import { useTranslation } from '../../contexts/TranslationContext';
import Loader from '../UC/Loader';

import api from '../../utils/api';

const ImportImages = ({
  images,
  setImages,
  files,
  setFiles,
  resetImportState,
  onUploadComplete,
  onImageEditorOpen,
  onImageEditorClose,
  isStandalone = false,
  onRemoveImage,
  updateImageAtIndex,
  isEditMode = false
}) => {
  const { language } = useTranslation();
  const fileInputRef = useRef(null);
  const { showPopup } = useGlobalPopup();

  // Additional refs
  const dropAreaRef = useRef(null);
  const inputFileRef = useRef(null);

  // State variables
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageToStandardize, setImageToStandardize] = useState(null);
  const [isImageEditorOpen, setIsImageEditorOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentImageType, setCurrentImageType] = useState("");
  const [currentOrientation, setCurrentOrientation] = useState("");
  const isUserInteracting = useRef(false);


  const svgIconAutoStandardize = (
    <svg

      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      fill="none"
      viewBox="0 0 20 24"
    >
      {/* Outer rectangle frame */}
      <rect
        x="1"
        y="1"
        width="18"
        height="22"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="3,2"
        fill="none"
        rx="2"
      />

      {/* Upper sole shape (toe area) */}
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1"
        fill="rgb(59, 130, 246, 0.1)"
        d="M10 3.795c-3.25 0-4.55 2.925-4.55 5.265 0 .936.26 1.755.52 2.574.13.468.26.936.26 1.287.13.351.26.702.52.936.39.351 1.04.468 1.69.468h3.12c.65 0 1.3-.117 1.69-.468.26-.234.39-.585.52-.936 0-.351.13-.819.26-1.287.26-.819.52-1.638.52-2.574 0-2.34-1.3-5.265-4.55-5.265z"
      />

      {/* Lower sole shape (heel area) */}
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1"
        fill="rgba(34, 197, 94, 0.15)"
        d="M7.324 15.524c0 .5616 0 1.1232.156 1.6848.156.7488.312 1.4976.624 2.0592.468.7488 1.248.7488 2.028.7488s1.56 0 2.028-.7488c.312-.5616.468-1.3104.624-2.0592.156-.5616.156-1.1232.156-1.6848H7.724z"
      />
    </svg>
  );

  // // showPopup function from global popup context
  // const showPopup = useCallback((popupConfig) => {
  //   if (setGlobalPopup && typeof setGlobalPopup === 'function') {
  //     setGlobalPopup(popupConfig);
  //   } else {
  //     console.error('showPopup is not available');
  //   }
  // }, [setGlobalPopup]);

  // Helper function to extract imageType and orientation from current type key
  const extractImageTypeAndOrientation = useCallback((typeKey) => {
    if (!typeKey || typeKey === "0") {
      return { imageType: "", orientation: "" };
    }

    switch (typeKey) {
      case global.config.Defines.ImageTypeKey.SHOE_SOLE_LEFT:
        return { imageType: "Sole", orientation: "Left" };
      case global.config.Defines.ImageTypeKey.SHOE_SOLE_RIGHT:
        return { imageType: "Sole", orientation: "Right" };
      case global.config.Defines.ImageTypeKey.SHOE_SOLE_UNDETERMINED:
        return { imageType: "Sole", orientation: "Undetermine" };
      case global.config.Defines.ImageTypeKey.SHOE_PRINT_LEFT:
        return { imageType: "Print", orientation: "Left" };
      case global.config.Defines.ImageTypeKey.SHOE_PRINT_RIGHT:
        return { imageType: "Print", orientation: "Right" };
      case global.config.Defines.ImageTypeKey.SHOE_PRINT_UNDETERMINED:
        return { imageType: "Print", orientation: "Undetermine" };
      case global.config.Defines.ImageTypeKey.SHOE_OTHER:
        return { imageType: "Other", orientation: "" };
      default:
        return { imageType: "", orientation: "" };
    }
  }, []);

  // Helper function to combine imageType and orientation into the final type key
  const combineImageTypeAndOrientation = useCallback((imageType, orientation) => {
    if (imageType === 'Other') {
      return global.config.Defines.ImageTypeKey.SHOE_OTHER;
    }

    if (imageType === 'Sole') {
      switch (orientation) {
        case 'Left':
          return global.config.Defines.ImageTypeKey.SHOE_SOLE_LEFT;
        case 'Right':
          return global.config.Defines.ImageTypeKey.SHOE_SOLE_RIGHT;
        case 'Undetermine':
          return global.config.Defines.ImageTypeKey.SHOE_SOLE_UNDETERMINED;
        default:
          return "0"; // No orientation selected yet
      }
    }

    if (imageType === 'Print') {
      switch (orientation) {
        case 'Left':
          return global.config.Defines.ImageTypeKey.SHOE_PRINT_LEFT;
        case 'Right':
          return global.config.Defines.ImageTypeKey.SHOE_PRINT_RIGHT;
        case 'Undetermine':
          return global.config.Defines.ImageTypeKey.SHOE_PRINT_UNDETERMINED;
        default:
          return "0"; // No orientation selected yet
      }
    }

    return "0"; // No image type selected yet
  }, []);

  // Separate effect for handling image preview changes
  useEffect(() => {
    if (images.length > 0 && images[previewImageIndex]) {
      // Update active image styling
      document.querySelectorAll('.image-import').forEach((el, idx) => {
        el.classList.remove('active');
        if (idx === previewImageIndex) {
          el.classList.add('active');
        }
      });

      const currentImage = images[previewImageIndex];
      const originPreviewEl = document.getElementById('imgPreviewOrigin');
      const standardizedPreviewEl = document.getElementById('imgPreviewStandardized');

      // Handle original image display
      if (originPreviewEl) {
        const imageUrl = currentImage.originalUrl || currentImage.base64 || '#';
        originPreviewEl.src = imageUrl;

        if (imageUrl !== '#') {
          originPreviewEl.onload = () => setIsLoading(false);
          originPreviewEl.onerror = () => setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }

      // Handle standardized image display
      if (standardizedPreviewEl) {
        if (currentImage.standardizedBase64) {
          standardizedPreviewEl.src = currentImage.standardizedBase64;
          standardizedPreviewEl.style.display = 'block';
        } else {
          standardizedPreviewEl.src = '#';
          standardizedPreviewEl.style.display = 'none';
        }
      }

    } else if (images.length === 0) {
      // Reset everything when no images
      const originPreviewEl = document.getElementById('imgPreviewOrigin');
      const standardizedPreviewEl = document.getElementById('imgPreviewStandardized');
      if (originPreviewEl) originPreviewEl.src = '#';
      if (standardizedPreviewEl) {
        standardizedPreviewEl.src = '#';
        standardizedPreviewEl.style.display = 'none';
      }
      setCurrentImageType("");
      setCurrentOrientation("");
      setIsLoading(false);
    }
  }, [images, previewImageIndex]);

  // Separate effect for syncing dropdown states with current image type - only when image changes
  useEffect(() => {
    if (images.length > 0 && images[previewImageIndex]) {
      const currentImage = images[previewImageIndex];
      const { imageType, orientation } = extractImageTypeAndOrientation(currentImage.type);

      // Set the dropdown states to match the current image
      setCurrentImageType(imageType || "");
      setCurrentOrientation(orientation || "");
    } else if (images.length === 0) {
      // Reset when no images
      setCurrentImageType("");
      setCurrentOrientation("");
    }
  }, [images, previewImageIndex, extractImageTypeAndOrientation]);  // Process a single file into an image object
  const handleFile = useCallback((file) => {
    if (!file.type.startsWith('image/')) return;

    let reader = new FileReader();
    reader.onload = (e) => {


      let img = {
        id: Math.random().toString(36).substring(7) + Date.now(),
        name: file.name,
        size: file.size,
        base64: e.target.result, // Origin
        standardizedBase64: null, // Initially no standardized image
        standardizedRect: null,
        type: 0,
        isExisting: false, // New image
        isRemove: false // Not marked for removal
      };
      setImages((prevImages) => {
        const newImages = [...prevImages, img];
        if (newImages.length === 1) { // If it's the first image, set it as preview
          setPreviewImageIndex(0);
        }
        return newImages;
      });
    };
    reader.readAsDataURL(file);
  }, [setImages, setPreviewImageIndex]);

  useEffect(() => {
    if (files.length > 0) {
      files.forEach(file => {
        handleFile(file);
      });
      setFiles([]); // Clear files after processing to prevent re-processing
    }
  }, [files, setFiles, handleFile]);

  // Handler for file input change
  const onFileChangeCapture = (e) => {
    let filesUpload = Array.from(e.target.files);
    filesUpload.forEach(file => {
      if (file.type.startsWith('image/')) {
        setFiles((prev) => [...prev, file]);
      }
    });
    e.target.value = null; // Reset file input
  };

  // Drag and drop event handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the drop area
    // (not just moving between child elements)
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);

      // only accept image png, jpg, jpeg, webp files
      if (droppedFiles.length === 0) return;

      if (droppedFiles.some(file => !file.type.startsWith('image/'))) {
        showPopup({
          type: 'error',
          title: language.importImages?.invalidFileTypeTitle || 'Invalid File Type',
          content: language.importImages?.invalidFileTypeMessage || 'Only image files (PNG, JPG, JPEG, WEBP) are allowed',
          options: [{ text: language.importImages?.okButton || 'OK', action: null, type: 'default' }],
        });
        return;
      }

      // popup message if image type is not supported
      if (droppedFiles.some(file => !['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type))) {
        showPopup({
          type: 'warning',
          title: language.importImages?.unsupportedImageTypeTitle || 'Unsupported Image Type',
          content: language.importImages?.unsupportedImageTypeMessage || 'Some images are not in supported formats (PNG, JPG, JPEG, WEBP). They will be ignored.',
          options: [{ text: language.importImages?.okButton || 'OK', action: null, type: 'default' }],
        });
      }

      // Process each dropped file
      droppedFiles.forEach(file => {
        if (file.type.startsWith('image/')) {
          setFiles((prev) => [...prev, file]);
        }
      });
    }
  };

  const loadPreviewImage = (index) => {
    if (previewImageIndex === index) return;

    

    // Reset user interaction flag when switching images
    isUserInteracting.current = false;

    setIsLoading(true);
    setPreviewImageIndex(index);

    // The useEffect will handle updating dropdown states
    // Loading will be turned off in the useEffect when image loads
    setTimeout(() => setIsLoading(false), 500); // Brief loading state for UX
  };

  const loadImageType = (type) => {

    const typeKey = type?.toString();

    // Handle all possible image type values from the dropdown
    switch (typeKey) {
      case global.config.Defines.ImageTypeKey.SHOE_SOLE_LEFT:
        return global.config.Defines.ImageTypeName.SHOE_SOLE_LEFT;
      case global.config.Defines.ImageTypeKey.SHOE_SOLE_RIGHT:
        return global.config.Defines.ImageTypeName.SHOE_SOLE_RIGHT;
      case global.config.Defines.ImageTypeKey.SHOE_SOLE_UNDETERMINED:
        return global.config.Defines.ImageTypeName.SHOE_SOLE_UNDETERMINED;
      case global.config.Defines.ImageTypeKey.SHOE_PRINT_LEFT:
        return global.config.Defines.ImageTypeName.SHOE_PRINT_LEFT;
      case global.config.Defines.ImageTypeKey.SHOE_PRINT_RIGHT:
        return global.config.Defines.ImageTypeName.SHOE_PRINT_RIGHT;
      case global.config.Defines.ImageTypeKey.SHOE_PRINT_UNDETERMINED:
        return global.config.Defines.ImageTypeName.SHOE_PRINT_UNDETERMINED;
      case global.config.Defines.ImageTypeKey.SHOE_OTHER:
        return global.config.Defines.ImageTypeName.SHOE_OTHER;
      case "0":
      default:
        return 'Undefined'; // Default for type 0 or unknown
    }
  }

  const selectImageType = useCallback((e) => {
    if (images.length === 0 || !images[previewImageIndex]) return;

    // Mark as user interaction
    isUserInteracting.current = true;

    const currentImage = images[previewImageIndex];
    const currentSelection = extractImageTypeAndOrientation(currentImage.type);
    const newImageType = e.target.value;

    // Update state immediately to show the dropdown
    setCurrentImageType(newImageType);

    currentImage.standardizedBase64 = null;
    currentImage.standardizedRect = null;
    //updateImageAtIndex(previewImageIndex, currentImage);

    // Determine new orientation based on image type change
    let newOrientation = "";
    if (newImageType === "Other") {
      // Other type doesn't need orientation
      newOrientation = "";
    } else if (newImageType === currentSelection.imageType) {
      // Same image type selected, keep current orientation
      newOrientation = currentSelection.orientation || "";
    } else {
      // Different image type selected, reset orientation to allow new selection
      newOrientation = "";
    }

    // Update orientation state
    setCurrentOrientation(newOrientation);

    // Apply the type immediately if conditions are met
    if (newImageType === "Other") {
      // Other type can be applied immediately (no orientation needed)
      const combinedType = combineImageTypeAndOrientation(newImageType, newOrientation);
      setImages((prevImages) => {
        const updatedImages = prevImages.map((img, index) =>
          index === previewImageIndex ? { ...img, type: combinedType } : img
        );
        return updatedImages;
      });
    } else if (newImageType && newOrientation) {
      // Sole/Print type with orientation can be applied immediately
      const combinedType = combineImageTypeAndOrientation(newImageType, newOrientation);
      setImages((prevImages) => {
        const updatedImages = prevImages.map((img, index) =>
          index === previewImageIndex ? { ...img, type: combinedType } : img
        );
        return updatedImages;
      });
    }
    // For Sole/Print without orientation, wait for orientation selection
  }, [images, previewImageIndex, extractImageTypeAndOrientation, combineImageTypeAndOrientation, setImages]);

  const selectOrientation = useCallback((e) => {
    if (images.length === 0 || !images[previewImageIndex]) return;

    // Mark as user interaction
    isUserInteracting.current = true;

    const newOrientation = e.target.value;

    // Update state immediately
    setCurrentOrientation(newOrientation);

    // remove current standardized image if any
    const currentImage = images[previewImageIndex];
    currentImage.standardizedBase64 = null;
    currentImage.standardizedRect = null;
    //updateImageAtIndex(previewImageIndex, currentImage);

    // Only apply the combined type if both image type and orientation are selected
    if (currentImageType && newOrientation) {
      const combinedType = combineImageTypeAndOrientation(currentImageType, newOrientation);

      setImages((prevImages) => {
        const updatedImages = prevImages.map((img, index) =>
          index === previewImageIndex ? { ...img, type: combinedType } : img
        );
        return updatedImages;
      });
    }
  }, [images, previewImageIndex, currentImageType, combineImageTypeAndOrientation, setImages]);

  const removeImage = (indexToRemove, e) => {
    e.stopPropagation(); // Prevent triggering the image selection click

    const imageToRemove = images[indexToRemove];
    if (!imageToRemove) return;

    // Check if the image has been standardized
    const hasStandardizedImage = imageToRemove.standardizedBase64;

    let confirmMessage = language.importImages?.removeImageConfirm?.replace('{name}', imageToRemove.name) || `Are you sure you want to remove "${imageToRemove.name}"?`;
    let title = language.importImages?.removeImageTitle || 'Remove Image';

    if (hasStandardizedImage) {
      confirmMessage = language.importImages?.removeStandardizedImageConfirm?.replace('{name}', imageToRemove.name) || `This image has been standardized. Are you sure you want to remove "${imageToRemove.name}" and lose the standardized version?`;
      title = language.importImages?.removeStandardizedImageTitle || 'Remove Standardized Image';
    }

    showPopup({
      type: 'warning',
      title: title,
      content: confirmMessage,
      options: [
        {
          text: language.importImages?.removeButton || 'Remove',
          action: () => {

            if (onRemoveImage && isNaN(imageToRemove.id) === false) {
              // Call the onRemoveImage callback to handle backend removal
              onRemoveImage(indexToRemove, imageToRemove.id);
            }

            setImages((prevImages) => {
              const newImages = prevImages.filter((_, index) => index !== indexToRemove);

              // Adjust preview index if necessary
              if (newImages.length === 0) {
                setPreviewImageIndex(0);
              } else if (indexToRemove <= previewImageIndex) {
                setPreviewImageIndex(Math.max(0, previewImageIndex - 1));
              }

              return newImages;
            });

            // Remove corresponding file if it exists
            setFiles((prevFiles) => {
              if (prevFiles[indexToRemove]) {
                return prevFiles.filter((_, index) => index !== indexToRemove);
              }
              return prevFiles;
            });
          },
          type: 'danger'
        },
        { text: language.importImages?.cancelButton || 'Cancel', action: null, type: 'default' }
      ],
      showCloseButton: true,
      backdrop: true
    });
  };

  // Handler for opening the ImageEditor modal
  const handleOpenImageEditor = () => {
    if (images.length > 0 && images[previewImageIndex]) {
      // first let find if the image has a standardized image id
      const selectedImage = images[previewImageIndex];

      setImageToStandardize(selectedImage);
      setIsImageEditorOpen(true);
    }
  };

  // Handler for closing the ImageEditor modal
  const handleCloseImageEditor = () => {
    setIsImageEditorOpen(false);
    setImageToStandardize(null);
  };

  // Handler for image standardization result
  const handleImageStandardized = (standardizeOutput) => { // Expect { base64: newBase64DataUrl, rect: newRectData }
    if (imageToStandardize && standardizeOutput) {

      const indexOfImage = images.findIndex(img => img.id === imageToStandardize.id);
      if (indexOfImage !== -1) {
        const updatedImage = {
          ...imageToStandardize,
          standardizedBase64: standardizeOutput.base64,
          standardizedRect: standardizeOutput.rect
        };

        updateImageAtIndex(indexOfImage, updatedImage);
      }
      else {
        setImages((prevImages) => {
          const updatedImages = prevImages.map(img =>
            img.id === imageToStandardize.id
              ? {
                ...img,
                standardizedBase64: standardizeOutput.base64,
                standardizedRect: standardizeOutput.rect
              }
              : img
          );
          return [...updatedImages];
        });
      }

    }
    setIsImageEditorOpen(false); // Close image editor if it was open
  };

  // New function to handle auto standardization for shoe soles
  const handleAutoStandardize = async () => {
    if (!images.length || !images[previewImageIndex]) return;

    setIsProcessing(true);
    setIsLoading(true);
    const currentImage = images[previewImageIndex];

    try {
      let bodyRequest = {
        ImageType: currentImage.type // Include the image type
      };

      let isID_Numberics = isNaN(currentImage.id) === false;

      // if editing an image, pass id, if not, pass base64
      if (isID_Numberics) {
        bodyRequest.id = currentImage.id; // Pass the image ID if available
      } else {
        bodyRequest.base64 = currentImage.base64.includes(',') ?
          currentImage.base64.split(',')[1] : // Remove the data:image/jpeg;base64, part if present
          currentImage.base64; // Use as-is if it's already base64
      }

      const _token = localStorage.getItem('FWToken');
      const response = await api.post(
        `${global.config.api.domain}/standardize/api/processimage/detectrect`,
        { Token: _token, ...bodyRequest }
      );

      const data = response.data;


      // Handle new response format: result = [...{Rects: {}, Base64: ''}]
      if (data && data.length > 0 && data[0].Base64) {

        // if auto standardize image from existing image, we need to call updatestandardized image api

        let isDoneStandardize = false;

        if (isID_Numberics && data[0].Rect) {
          // Call update API to save standardized image
          const updateResponse = await api.post(
            `${global.config.api.domain}/standardize/api/processimage/updatestandardize`,
            {
              Token: _token,
              id: currentImage.id,
              Rect: data[0].Rect,
              ObjectTableName: 'evidence',
              Type: currentImage.type
            }
          );

          if (updateResponse.status === 200 || updateResponse.status === 204) {
            isDoneStandardize = true;
          }
        }
        else {
          isDoneStandardize = true;
        }

        // Update the image with the standardized version
        if (isDoneStandardize)
          setImages(prevImages =>
            prevImages.map(img =>
              img.id === currentImage.id
                ? {
                  ...img,
                  standardizedBase64: `data:image/jpeg;base64,${data[0].Base64}`,
                  standardizedRect: null
                }
                : img
            )
          );
      } else {
        showPopup({
          type: 'error',
          title: language.importImages?.autoStandardizationFailedTitle || 'Auto-standardization Failed',
          content: language.importImages?.autoStandardizationFailedMessage || 'Failed to auto-standardize the image. Please try manual standardization.',
          options: [{ text: language.importImages?.okButton || 'OK', action: null, type: 'default' }],
          showCloseButton: true,
          backdrop: true
        });
      }
    } catch (error) {
      console.error('Error during auto standardization:', error);
      showPopup({
        type: 'error',
        title: language.importImages?.autoStandardizationFailedTitle || 'Auto-standardization Failed',
        content: language.importImages?.autoStandardizationFailedMessage || 'Failed to auto-standardize the image. Please try manual standardization.',
        options: [{ text: language.importImages?.okButton || 'OK', action: null, type: 'default' }],
        showCloseButton: true,
        backdrop: true
      });
    } finally {
      setIsProcessing(false);
      setIsLoading(false);
    }
  };

  const currentImageForTypeSelection = images[previewImageIndex];

  return (
    <div className="import-images">
      {isLoading &&
        // Show loading overlay only if isLoading is true
        <div className="loading-overlay">
          <Loader />
        </div>
      }
      <div className={images && images.length > 0 ? "upload-images" : "upload-images empty"}>
        <div
          ref={dropAreaRef}
          className={images && images.length > 0 ? "drag-drop hide" : `drag-drop ${isDragging ? 'dragging-over' : ''}`}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >      <div className="drag-drop-title">
            <h4>{language.importImages?.dragDropTitle || 'Drag and Drop Images Here'}</h4>
            <small>{language.importImages?.supportedFormats || 'Supported formats: JPG, PNG, WEBP'}</small>
          </div>
          <div className="drag-drop-content">
            <div className="drag-drop-icon">
              <i className="fa fa-cloud-upload" aria-hidden="true"></i>
            </div>
            <div className="drag-drop-text">
              <p>{language.importImages?.orText || 'or'}</p>
            </div>
            <div className="files-input">
              <input type="file" ref={inputFileRef} onChange={onFileChangeCapture} name="fileImgs" multiple="multiple" accept="image/*" />
            </div>
            <div className="drag-drop-button">
              <button className="btn btn-default" id="btnUploadFile" type="button"
                onClick={() => { inputFileRef.current.click() }}>
                <i className="fa fa-upload" aria-hidden="true"></i>
                {language.importImages?.uploadImages || 'Upload Images'}
              </button>
            </div>
          </div>
        </div>
        <div id="dvLoadedImages" className={images.length > 0 ? "loaded-images" : "loaded-images empty"}>
          <div className="array-loaded-images">
            <div id="lstBrowseImages">
              {images && images.map((img, index) => (
                <div
                  key={img.id}
                  className={`image-import ${index === previewImageIndex ? 'active' : ''} ${parseInt(img.type) === 0 ? 'undefined' : ''}`}
                  onClick={() => { loadPreviewImage(index) }}
                >
                  <div className="loaded-image-preview">
                    <img src={img.base64} alt={img.name}></img>
                    <button
                      className="remove-image-btn"
                      onClick={(e) => removeImage(index, e)}
                      title={`Remove ${img.name}${img.standardizedBase64 ? ' (has standardized version)' : ''}`}
                      aria-label={`Remove image ${img.name}`}
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  </div>
                  <div className="loaded-image-info">
                    <div className="loaded-image-type">
                      {loadImageType(img.type)}
                    </div>
                  </div>
                </div>
              ))}

              {images.length > 0 ? (
                <div className="image-import more">
                  <div className="more-images" onClick={() => { inputFileRef.current.click() }}>
                    <i className="fa fa-plus"></i>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <div className="focus-area">
            {/* Image comparison view moved to top */}
            <div className="focus-image-previews">
              <div className="focus-image-pane origin-pane">
                <h4>Original Image</h4>
                <div className="image-container">
                  <img src="#" alt="Original Preview" id="imgPreviewOrigin" />
                </div>

                {/* Image Type Selection inside Original Image pane */}
                {images.length > 0 && currentImageForTypeSelection && !isEditMode && (
                  <div className="image-type-selection-inline">
                    <div className="type-selection-controls">
                      {/* Side by side layout for Image Type and Orientation */}
                      <div className="selection-row">
                        {/* Image Type Selection */}
                        <div className="selection-group">
                          <label htmlFor="cboImageType" className="type-label">
                            {language.importImages?.selectImageType || 'Image Type'}
                          </label>
                          <select
                            id="cboImageType"
                            value={currentImageType}
                            onChange={selectImageType}
                            className="type-dropdown-inline"
                            aria-label="Select image type for processing"
                          >
                            <option value="">{language.importImages?.selectImageTypePlaceholder || '-- Select Type --'}</option>
                            <option value="Sole">{global.config.Defines.ImageTypeName.SHOE_SOLE}</option>
                            <option value="Print">{global.config.Defines.ImageTypeName.SHOE_PRINT}</option>
                            {/* <option value="Other">{global.config.Defines.ImageTypeName.SHOE_OTHER}</option> */}
                          </select>
                        </div>

                        {/* Orientation Selection - only show when image type is not Other */}
                        {currentImageType && currentImageType !== "Other" && (
                          <div className="selection-group">
                            <label htmlFor="cboOrientation" className="type-label">
                              {language.importImages?.selectOrientation || 'Orientation'}
                            </label>
                            <select
                              id="cboOrientation"
                              value={currentOrientation}
                              onChange={selectOrientation}
                              className="type-dropdown-inline"
                              aria-label="Select orientation for processing"
                            >
                              <option value="">{language.importImages?.selectOrientationPlaceholder || '-- Select --'}</option>
                              <option value="Left">Left</option>
                              <option value="Right">Right</option>
                              {currentImageType === "Print" && (
                                <option value="Undetermine">Undetermined</option>
                              )}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Image Type Selection for existing images in edit mode - show only for non-existing images */}
                {images.length > 0 && currentImageForTypeSelection && isEditMode && !currentImageForTypeSelection.isExisting && (
                  <div className="image-type-selection-inline">
                    <div className="type-selection-controls">
                      {/* Side by side layout for Image Type and Orientation */}
                      <div className="selection-row">
                        {/* Image Type Selection */}
                        <div className="selection-group">
                          <label htmlFor="cboImageType" className="type-label">
                            {language.importImages?.selectImageType || 'Image Type'}
                          </label>
                          <select
                            id="cboImageType"
                            value={currentImageType}
                            onChange={selectImageType}
                            className="type-dropdown-inline"
                            aria-label="Select image type for processing"
                          >
                            <option value="">{language.importImages?.selectImageTypePlaceholder || '-- Select Type --'}</option>
                            <option value="Sole">{global.config.Defines.ImageTypeName.SHOE_SOLE}</option>
                            <option value="Print">{global.config.Defines.ImageTypeName.SHOE_PRINT}</option>
                            {/* <option value="Other">{global.config.Defines.ImageTypeName.SHOE_OTHER}</option> */}
                          </select>
                        </div>

                        {/* Orientation Selection - only show when image type is not Other */}
                        {currentImageType && currentImageType !== "Other" && (
                          <div className="selection-group">
                            <label htmlFor="cboOrientation" className="type-label">
                              {language.importImages?.selectOrientation || 'Orientation'}
                            </label>
                            <select
                              id="cboOrientation"
                              value={currentOrientation}
                              onChange={selectOrientation}
                              className="type-dropdown-inline"
                              aria-label="Select orientation for processing"
                            >
                              <option value="">{language.importImages?.selectOrientationPlaceholder || '-- Select --'}</option>
                              <option value="Left">Left</option>
                              <option value="Right">Right</option>
                              {currentImageType === "Print" && (
                                <option value="Undetermine">Undetermined</option>
                              )}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action buttons below image type selection */}
                {(() => {
                  if (!images || images.length === 0 || !images[previewImageIndex]) {
                    return null;
                  }

                  const currentImage = images[previewImageIndex];

                  // For existing images in edit mode
                  if (isEditMode && currentImage.isExisting) {
                    // Only show Manual Standardize for non-Other types
                    // Use the image's actual type, not the currentImageType from dropdown
                    const imageTypeKey = currentImage.type?.toString();
                    const isOtherType = imageTypeKey === global.config.Defines.ImageTypeKey.SHOE_OTHER?.toString();

                    if (isOtherType) {
                      return null;
                    }

                    // Show Manual Standardize button for existing non-Other images
                    return (
                      <div className="image-actions">
                        <button
                          className="btn btn-primary"
                          onClick={handleOpenImageEditor}
                          aria-label="Manual standardize image"
                        >
                          <i className="fa fa-hand-paper-o" aria-hidden="true"></i>
                          <span>Manual Standardize</span>
                        </button>
                      </div>
                    );
                  }

                  // For new images or non-edit mode: require type selection
                  if (!currentImageType) {
                    return null;
                  }

                  // Don't show buttons for Other type or if no orientation is selected for Sole/Print types
                  if (currentImageType === "Other" || !currentOrientation) {
                    return null;
                  }

                  return (
                    <div className="image-actions">
                      {/* Auto Standardize for Shoe Sole types */}
                      {currentImageType === "Sole" && (
                        <button
                          className={`btn btn-success ${isProcessing ? 'processing' : ''}`}
                          onClick={handleAutoStandardize}
                          disabled={isProcessing}
                          aria-label="Auto standardize image using AI detection"
                        >
                          {isProcessing ? (
                            <>
                              <i className="fa fa-spinner fa-spin"></i>
                              <span>Processing...</span>
                            </>
                          ) : (
                            <>
                              {svgIconAutoStandardize}
                              <span>Auto Standardize</span>
                            </>
                          )}
                        </button>
                      )}

                      {/* Edit Standardize for Shoe Sole types */}
                      {currentImageType === "Sole" && (
                        <button
                          className="btn btn-primary"
                          onClick={handleOpenImageEditor}
                          aria-label="Edit and manually standardize image"
                        >
                          <i class="fa fa-hand-paper-o" aria-hidden="true"></i>
                          <span>Manual Standardize</span>
                        </button>
                      )}

                      {/* Manual Standardize for Shoe Print types */}
                      {currentImageType === "Print" && (
                        <button
                          className="btn btn-primary"
                          onClick={handleOpenImageEditor}
                          aria-label="Manual standardize image"
                        >
                          <i class="fa fa-hand-paper-o" aria-hidden="true"></i>
                          <span>Manual Standardize</span>
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>            {/* Arrow and standardized pane only show when there's a standardized image or processing is available (exclude Other) */}
              {(() => {
                if (!images || images.length === 0 || !images[previewImageIndex]) {
                  return null;
                }

                const currentImage = images[previewImageIndex];

                // For existing images in edit mode
                if (isEditMode && currentImage.isExisting) {
                  // Show standardized pane if has standardized image or is non-Other type
                  const imageTypeKey = currentImage.type?.toString();
                  const isOtherType = imageTypeKey === global.config.Defines.ImageTypeKey.SHOE_OTHER?.toString();

                  const shouldShowStandardizedPane = currentImage.standardizedBase64 || !isOtherType;

                  if (!shouldShowStandardizedPane) {
                    return null;
                  }
                } else {
                  // For new images or non-edit mode: require type selection
                  if (!currentImageType) {
                    return null;
                  }

                  // Show standardized pane if:
                  // 1. Has standardized image, OR
                  // 2. Image type is Sole/Print with orientation selected (processing is available)
                  const shouldShowStandardizedPane = currentImageForTypeSelection?.standardizedBase64 ||
                    (currentImageType !== "Other" && currentOrientation);

                  if (!shouldShowStandardizedPane) {
                    return null;
                  }
                }

                return (
                  <>
                    <div className="image-arrow">
                      <i className="fa fa-arrow-right"></i>
                    </div>

                    <div className="focus-image-pane standardized-pane">
                      <h4>Standardized Image</h4>
                      <div className="image-container">
                        {currentImageForTypeSelection?.standardizedBase64 ? (
                          <img src={currentImageForTypeSelection?.standardizedBase64} alt="Standardized Preview" id="imgPreviewStandardized" />
                        ) : (
                          <div className="no-standardized-placeholder">
                            <i className="fa fa-image"></i>
                            <p>
                              {currentImageType === "Print"
                                ? 'Use Manual Standardize button to process this image'
                                : 'Use Auto Standardize button to process this image'}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Processing status indicator */}
            {isProcessing && (
              <div className="processing-status">
                <div className="status-content">
                  <i className="fa fa-cog fa-spin"></i>
                  <span>{language.importImages?.processingImage || 'Processing your image...'}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isImageEditorOpen && imageToStandardize && (
        <div className="image-editor-overlay">
          <ImageEditor
            selectedImage={{
              src: imageToStandardize.originalUrl || imageToStandardize.base64,
              id: imageToStandardize.id,
              standardizedImageId: imageToStandardize.standardizedImageId, // Pass existing standardized image ID if available
              type: imageToStandardize.type
            }}
            onDetectRect={async (standardizeOutput) => {
              // Handle manual standardization result from ImageEditor
              if (standardizeOutput) {
                // For existing images with standardized image ID
                if (standardizeOutput.standardizedImageId) {
                  try {
                    handleImageStandardized({
                      base64: standardizeOutput.standardizedImageUrl,
                      rect: standardizeOutput.rect,
                      standardizedImageId: standardizeOutput.standardizedImageId
                    });
                  } catch (error) {
                    console.error('Error fetching standardized image:', error);
                    // Still call handler with the available data
                    handleImageStandardized(standardizeOutput);
                  }
                }
                // For new images or fallback cases
                else if (standardizeOutput.base64 || standardizeOutput.rect) {
                  // If we have both base64 and rect (new format), use them directly
                  if (standardizeOutput.base64 && standardizeOutput.rect) {
                    handleImageStandardized(standardizeOutput);
                  } else if (standardizeOutput.topLeft) {
                    // Fallback for old format: create standardized output object
                    const fallbackOutput = {
                      base64: imageToStandardize.originalUrl || imageToStandardize.base64, // Use original or base64 as fallback
                      rect: standardizeOutput
                    };
                    handleImageStandardized(fallbackOutput);
                  }
                }
              }
            }}
            onClose={handleCloseImageEditor}
          />
        </div>
      )}
    </div>
  );
};

export default ImportImages;